import * as React from 'react'
import { Link, Route, RouteComponentProps } from 'react-router-dom'
import { Column, Row, useExpanded, usePagination, useTable } from 'react-table'
import {
  IDriverDeleteSessionObject,
  IDriverEditSessionObject,
  IDriverSessionFilters,
  IDriverSessionHistoryFilters,
  IDriverSessionSearchObject,
  IDriverSessionTripsHistory
} from '../../types'
import ReactTablePagination from '../ui/ReactTablePagination'
import moment from 'moment'
import UIMessages from 'src/utils/ui-messages'
import { addTime, metersToMiles, secondsToHours } from 'src/utils'
import EditIcon from '../icons/ui/Edit'
import DeleteIcon from '../icons/ui/TrashGrey'
import SessionHistoryEditTimeModal from 'src/containers/drivers/SessionHistoryEditTimeModal'
import SessionHistoryDeleteSessionModal from './SessionHistoryDeleteSessionModal'
import PreloaderCircle from '../ui/PreloaderCircle'
import { MoonLoader } from 'react-spinners'
import { NotificationProvider } from '../ui/notification-provider'

export interface ITableDriverSessionProps extends RouteComponentProps {
  currentOrganizationId: string
  error: string
  filters: IDriverSessionFilters
  isFetching: boolean
  openDeleteModal: boolean
  ownProps: RouteComponentProps<any>
  sessionHistory: IDriverSessionSearchObject[]
  totalSessions: number
  deleteSession: (dto: IDriverEditSessionObject) => Promise<any>
  getCheckInCheckOut: (filters: IDriverSessionHistoryFilters) => Promise<any>
  getDriversSessionList: (filters: IDriverSessionFilters) => Promise<any>
  getManyTripsPerOperator: (filters: IDriverSessionHistoryFilters) => Promise<any>
}

export interface ITableDriversState {
  isArchived: boolean
  pages: number
  search: string
}

interface SessionTableProps {
  accumulatedHours: any[]
  data: any
  loading: boolean
  setToDelete: (args: { delete: boolean; element: any }) => void
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  filters: IDriverSessionFilters
  getCheckInCheckOut: any
  loading: boolean
  openDeleteModal: boolean
  pageCount: number
  renderRowSubComponent: any
  totalCount: number
  fetchData: (params: { filters: IDriverSessionFilters; pageIndex: number; pageSize: number }) => void
  getManyTripsPerOperator: (filters: IDriverSessionHistoryFilters) => Promise<any>
}

export const getTotalDetails = (sessionHistory: IDriverSessionSearchObject[]) => {
  let revenue = 0
  let fare = 0
  let trip = 0
  let service = 0
  let driving = 0
  let estimated = 0
  let distance = 0
  let completed = 0
  let assigned = 0
  let duration = '00:00:00'
  let breakHours = 0
  let totalHoursWorked = 0
  sessionHistory.forEach(item => {
    fare = fare + item.fare
    revenue = revenue + item.revenue
    trip = trip + (item.tripDuration || 0)
    service = service + (item.serviceDuration || 0)
    driving = driving + (item.drivingDuration || 0)
    estimated = estimated + (item.estimatedDuration || 0)
    distance = distance + (item.distanceDuration || 0)
    completed = completed + (item.completedTripsCount || 0)
    assigned = assigned + (item.assignedTripsCount || 0)
    duration = addTime(duration, item.duration)
    breakHours = breakHours + (item.breakHours || 0)
    totalHoursWorked = totalHoursWorked + (item.totalHoursWorked || 0)
  })
  const totalHours = trip / 3600
  return {
    assigned,
    breakHours,
    completed,
    distance,
    driving,
    duration,
    estimated,
    fare,
    revenue,
    service,
    totalHours,
    trip,
    totalHoursWorked
  }
}
function SubRowAsync(data: any) {
  const [loading, setLoading] = React.useState(true)
  const [driverTripsLoading, setDriverTripsLoading] = React.useState(true)
  const [driverTrips, setDriverTrips] = React.useState([])
  const [accumulatedHours, setAccumulatedHours] = React.useState([])
  const [toDelete, setToDelete] = React.useState<any>({ delete: false, element: null })

  const notification: any = React.useContext(NotificationProvider)

  React.useEffect(() => {
    data
      .getCheckInCheckOut(data.filters)
      .then((data: any) => {
        setAccumulatedHours(data.items)
        setLoading(false)
      })
      .catch(() => setLoading(false))

    data
      .getManyTripsPerOperator(data.filters)
      .then((data: any) => {
        setDriverTrips(data.items)
        setDriverTripsLoading(false)
      })
      .catch(() => setDriverTripsLoading(false))
  }, [])

  const closeDeleteModal = () => {
    setToDelete({ delete: false, element: null })
  }

  const getDeleteModalMessage = () => {
    const sessionDateRange = toDelete.element.originalTimeRange.split(' - ')
    const isSessionSameDay =
      moment(sessionDateRange[0]).format('YYYY-MM-DD') === moment(sessionDateRange[1]).format('YYYY-MM-DD')
    const multiDaySessionMessage = `This session spans multiple days (${moment(sessionDateRange[0]).format(
      'MM/DD/YYYY HH:mm'
    )} - ${moment(sessionDateRange[1]).format('MM/DD/YYYY HH:mm')}). ${UIMessages.driver.deleteModalMessage}`

    return !toDelete.element.checkOutTime || toDelete.element.checkOutTime === 'Invalid date'
      ? 'Check out time cannot be empty'
      : !isSessionSameDay
      ? multiDaySessionMessage
      : UIMessages.driver.deleteModalMessage
  }

  const disableDeleteButton = () => {
    return !toDelete.element.checkOutTime || toDelete.element.checkOutTime === 'Invalid date' ? true : false
  }

  const deleteSession = async () => {
    if (toDelete.element) {
      console.log(toDelete)
      const clockInDate = new Date(toDelete.element.workedDate)
      const clockOutDate = new Date(toDelete.element.workedDate)

      const clockInTime = toDelete.element.checkInTime ? toDelete.element.checkInTime.split(':') : []
      if (clockInTime.length > 0) {
        clockInDate.setHours(+clockInTime[0], +clockInTime[1], +clockInTime[2])
      }
      const clockOutTime = toDelete.element.checkOutTime ? toDelete.element.checkOutTime.split(':') : []
      if (clockOutTime) {
        clockOutDate.setHours(+clockOutTime[0], +clockOutTime[1], +clockOutTime[2])
      }

      const session: IDriverDeleteSessionObject = {
        id: toDelete.element.id
      }

      console.log(session)

      data
        .deleteSession(session)
        .then(() => {
          notification.addNotification({
            title: 'Successfully deleted driver session.',
            level: 'success',
            autoDismiss: 5
          })
          window.location.reload()
        })
        .catch(() => {
          notification.addNotification({
            title: 'An error occurred while driver session. Please try again.',
            level: 'error',
            autoDismiss: 5
          })
        })
      closeDeleteModal()
    }
  }

  const SessionTypeTable: React.FC<SessionTableProps> = ({ data, accumulatedHours, loading, setToDelete }) => {
    const sortedAccumulatedHours =
      accumulatedHours.length > 0
        ? [...accumulatedHours].sort((a, b) => {
            const dateA =
              a.checkInTime && !isNaN(new Date(a.checkInTime).getTime()) ? new Date(a.checkInTime).getTime() : 0
            const dateB =
              b.checkInTime && !isNaN(new Date(b.checkInTime).getTime()) ? new Date(b.checkInTime).getTime() : 0
            return dateA - dateB
          })
        : []
    const tableDetails = {
      after_hours: {
        header: UIMessages.driver.afterHours,
        label: 'After Hours'
      },
      break: {
        header: UIMessages.driver.breakTIme,
        label: 'Break Time'
      },
      standby: {
        header: 'Standby',
        label: 'Clocked In Time'
      },
      work_time: {
        header: UIMessages.driver.dispatch,
        label: 'Dispatch Time'
      }
    }

    return (
      <>
        {!loading && sortedAccumulatedHours && sortedAccumulatedHours.length ? (
          sortedAccumulatedHours.map((session, index) => {
            const {
              checkInCoordinates,
              checkInTime,
              checkOutCoordinates,
              checkOutTime,
              distanceTravel,
              totalHoursWorked,
              sessionId,
              sessionType,
              workedDate,
              timeLabel
            } = session

            const prevItemSessionType = index !== 0 ? sortedAccumulatedHours[index - 1].sessionType : ''
            return (
              <>
                {sessionType !== prevItemSessionType && (
                  <tr key={`${sessionId}-header`}>
                    <td className='bg-light-grey9 text-center' colSpan={8}>
                      <span className='font-light-grey fs-14 weight-400'>
                        {tableDetails[sessionType as keyof typeof tableDetails].header}
                      </span>
                    </td>
                  </tr>
                )}
                <tr key={sessionId}>
                  <td
                    className='dsh-table-col-clock-in'
                    data-label={tableDetails[sessionType as keyof typeof tableDetails].label}
                  >
                    {getTime(checkInTime, checkOutTime)}
                  </td>
                  <td className='dsh-table-col-label' data-label='Label'>
                    {timeLabel ? timeLabel : '-'}
                  </td>
                  <td className='dsh-table-col-total' data-label='Total'>
                    {secondsToHours(totalHoursWorked)}
                  </td>
                  <td className='dsh-table-col-coordinates' data-label='Check-in Coordinates'>
                    {checkInCoordinates || '--'}
                  </td>
                  <td className='dsh-table-col-coordinates' data-label='Check-out Coordinates'>
                    {checkOutCoordinates || '--'}
                  </td>
                  <td className='dsh-table-col-distance' data-label='Distance Traveled'>
                    {`${distanceTravel ? metersToMiles(distanceTravel) : 0.0} miles`}
                  </td>
                  <td className='dsh-table-col-actions'>
                    <Link
                      className='dsh-table-edit-time-link'
                      to={{
                        pathname: `${data.routeProps.match.url}/edit-time`,
                        state: {
                          element: session,
                          displayCheckInDateTime: getDisplayDateTime(workedDate, checkInTime),
                          displayCheckOutDateTime:
                            !checkOutTime || checkOutTime === 'Invalid date'
                              ? getDisplayDateTime(workedDate, checkInTime)
                              : getDisplayDateTime(workedDate, checkOutTime)
                        }
                      }}
                    >
                      <EditIcon className='dsh-table-edit-time-icon icon--small svg-stroke-grey3 mr1' />
                    </Link>
                    <DeleteIcon
                      className='dsh-table-delete-time-icon cursor--pointer icon--small'
                      onClick={() => setToDelete({ delete: true, element: session })}
                    />
                  </td>
                </tr>
              </>
            )
          })
        ) : !loading ? (
          <div className='flex align-center justify-center p2 h5 font-grey2'>
            <p className='h5 font-grey2'>No Check-In/Check-Out Data Found.{loading}</p>
          </div>
        ) : (
          <tr className='flex justify-center p2'>
            <td>
              <div>
                <PreloaderCircle loading={true} />
              </div>
            </td>
          </tr>
        )}
      </>
    )
  }

  const sortedDriverTrips =
    driverTrips.length > 0
      ? [...driverTrips].sort((a: IDriverSessionTripsHistory, b: IDriverSessionTripsHistory) => {
          const dateA =
            a.pickup_time && !isNaN(new Date(a.pickup_time).getTime()) ? new Date(a.pickup_time).getTime() : 0
          const dateB =
            b.pickup_time && !isNaN(new Date(b.pickup_time).getTime()) ? new Date(b.pickup_time).getTime() : 0
          return dateA - dateB
        })
      : []

  const checkStatusForNotAvailable = (status: string) => {
    if (
      status === 'CANCELLED' ||
      status === 'CANCEL_BILLABLE' ||
      status === 'MANUALLY_COMPLETED' ||
      status === 'COMPLETED'
    ) {
      return true
    } else {
      return false
    }
  }

  return (
    <React.Fragment>
      {toDelete.delete && (
        <SessionHistoryDeleteSessionModal
          disableDeleteButton={disableDeleteButton()}
          message={getDeleteModalMessage()}
          onClose={closeDeleteModal}
          onDelete={deleteSession}
        />
      )}
      <div className='dsh-table-accumulated-hours'>
        <table className='tableInner'>
          <thead>
            <tr key={'header'}>
              <th className='dsh-table-col-clock-in' scope='col'>
                {UIMessages.driver.driverClockedIn}
              </th>
              <th className='dsh-table-col-label' scope='col'>
                {UIMessages.driver.label}
              </th>
              <th className='dsh-table-col-total' scope='col'>
                {UIMessages.driver.total}
              </th>
              <th className='dsh-table-col-coordinates' scope='col'>
                {UIMessages.driver.checkInCoordinates} (Lat, Long)
              </th>
              <th className='dsh-table-col-coordinates' scope='col'>
                {UIMessages.driver.checkOutCoordinates} (Lat, Long)
              </th>
              <th className='dsh-table-col-distance' scope='col'>
                {UIMessages.driver.distanceTraveled}
              </th>
              <th className='dsh-table-col-actions' scope='col'>
                {'Actions'}
              </th>
            </tr>
          </thead>
          <tbody>
            <SessionTypeTable
              accumulatedHours={accumulatedHours}
              data={data}
              loading={loading}
              setToDelete={setToDelete}
            />
          </tbody>
        </table>
      </div>
      <div className='dsh-table-trip-details'>
        <table className='mt1 tableInner'>
          <thead>
            <tr key={'header-4'}>
              <th className='dsh-table-col-trip-id' scope='col'>
                {UIMessages.trip.id}
              </th>
              <th className='dsh-table-col-indicator' scope='col'>
                {'Indicator'}
              </th>
              <th className='dsh-table-col-pickup' scope='col'>
                {UIMessages.trip.scheduledPickUpTime}
              </th>
              <th className='dsh-table-col-dropoff' scope='col'>
                {UIMessages.trip.scheduledDropOffTime}
              </th>
              <th className='dsh-table-col-duration' scope='col'>
                {UIMessages.driver.duration}
              </th>
              <th className='dsh-table-col-estimated-duration' scope='col'>
                {UIMessages.driver.estimatedDuration}
              </th>
              <th className='dsh-table-col-service' scope='col'>
                {UIMessages.trip.service}
              </th>
              <th className='dsh-table-col-product' scope='col'>
                {UIMessages.trip.product}
              </th>
              <th className='dsh-table-col-distance' scope='col'>
                {UIMessages.trip.distance}
              </th>
              <th className='dsh-table-col-waiting-time' scope='col'>
                {UIMessages.service.allottedWaitingTime}
              </th>
              <th className='dsh-table-col-status' scope='col'>
                {UIMessages.common.status}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className='flex justify-center p2'>
                <td>
                  <div>
                    <PreloaderCircle loading={true} />
                  </div>
                </td>
              </tr>
            )}
            {!driverTripsLoading && (
              <React.Fragment>
                {sortedDriverTrips && sortedDriverTrips.length ? (
                  sortedDriverTrips.map((element: IDriverSessionTripsHistory) => {
                    const {
                      dropoff_time,
                      distance,
                      duration,
                      estimated_duration,
                      id,
                      last_status,
                      pickup_time,
                      product_name,
                      service_name,
                      allotted_loading_time
                    } = element

                    return (
                      <tr key={id}>
                        <td className='dsh-table-col-trip-id' data-label='id'>
                          <Link className='dsh-table-cell-trip-id-link' to={{ pathname: `/trips/${id}` }}>
                            <span className='TransactionId h5 font-blue underline'>{id.slice(-8)}</span>
                          </Link>
                        </td>
                        <td className='dsh-table-col-indicator' data-label='Indicator'>
                          {indicator(element)}
                        </td>
                        <td className='dsh-table-col-pickup dsh-table-cell-pickup' data-label='Service'>
                          {moment(pickup_time).isValid() && checkStatusForNotAvailable(last_status)
                            ? moment(pickup_time).format('MM/DD/YYYY H:mm')
                            : 'Not Available'}
                        </td>
                        <td className='dsh-table-col-dropoff dsh-table-cell-dropoff' data-label='Service'>
                          {moment(dropoff_time).isValid() && checkStatusForNotAvailable(last_status)
                            ? moment(dropoff_time).format('MM/DD/YYYY H:mm')
                            : 'Not Available'}
                        </td>
                        <td className='dsh-table-col-duration' data-label='Duration'>
                          {duration ? secondsToHours(Number(duration)) : 0}
                        </td>
                        <td className='dsh-table-col-estimated-duration' data-label='Duration'>
                          {estimated_duration ? secondsToHours(estimated_duration) : 0}
                        </td>
                        <td className='dsh-table-col-service dsh-table-cell-service' data-label='Service'>
                          {service_name}
                        </td>
                        <td className='dsh-table-col-product dsh-table-cell-product' data-label='Product'>
                          {product_name}
                        </td>
                        <td className='dsh-table-col-distance' data-label='Distance'>
                          {metersToMiles(Number(distance)) + ' miles'}
                        </td>
                        <td className='dsh-table-col-waiting-time' data-label='Allotted Waiting Time'>
                          {allotted_loading_time}
                        </td>
                        <td className='dsh-table-col-status' data-label='Status'>
                          {last_status}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <div className='flex align-center justify-center p2 h5 font-grey2'>
                    <p className='h5 font-grey2'>No Trips Found.</p>
                  </div>
                )}
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  )
}

function getTime(checkInDate: string, checkOutDate: string) {
  const checkInTime = getAbsoluteTime(checkInDate)
  const checkOutTime = getAbsoluteTime(checkOutDate)

  if (!checkOutTime || checkOutTime === 'Invalid date' || typeof checkOutDate === 'object') {
    return `${checkInTime} - `
  }

  return `${checkInTime} - ${checkOutTime}`
}

function getAbsoluteTime(date: string) {
  const dateInstance = new Date(date)
  return `${dateInstance
    .getHours()
    .toString()
    .padStart(2, '0')}:${dateInstance
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}

function getDisplayDateTime(workedDate: string, time: string) {
  const dateTime = new Date(workedDate)
  const timeSplit = time ? time.split(':') : []
  if (timeSplit.length > 0) {
    dateTime.setHours(+timeSplit[0], +timeSplit[1], +timeSplit[2])
  }

  return moment(dateTime).format('YYYY-MM-DD H:mm')
}

function indicator(tripSession: any) {
  const { pickup_time, completed_time } = tripSession
  const format = 'H:mm:ss'
  const pickUpTime = moment(moment(pickup_time).format(format), format)
  const dropOffTime = moment(moment(completed_time).format(format), format)
  const afterTime = moment('20:00:00', format) //8pm
  const beforeTime = moment('06:00:00', format) //6am
  const isPickUpAfterHours = pickUpTime.isAfter(afterTime) || pickUpTime.isBefore(beforeTime)
  const isDropOffAfterHours = dropOffTime.isAfter(afterTime) || dropOffTime.isBefore(beforeTime)

  if (tripSession.group_ride_id && tripSession.group_ride_id != null) {
    return 'G'
  }
  if (isPickUpAfterHours && isDropOffAfterHours) {
    return 'AH'
  } else {
    return 'R'
  }
}

function editTime(props: any, operatorId: string) {
  return <SessionHistoryEditTimeModal {...props} operatorId={operatorId} />
}

function Table<T extends object>({
  columns,
  data,
  filters,
  getCheckInCheckOut,
  loading,
  openDeleteModal,
  pageCount: controlledPageCount,
  renderRowSubComponent,
  totalCount,
  fetchData,
  getManyTripsPerOperator
}: IProps<T>) {
  const {
    canNextPage,
    canPreviousPage,
    headerGroups,
    page,
    rows,
    state: { pageIndex, pageSize },
    visibleColumns,
    getTableBodyProps,
    getTableProps,
    nextPage,
    prepareRow,
    previousPage,
    setPageSize
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useExpanded,
    usePagination
  )

  const filterDuplicateRows = React.useMemo(() => {
    const rowsArr = rows.reduce<Array<Row<T>>>((acc, session, index) => {
      const { checkInTime } = (session.original as IDriverSessionSearchObject) || {}

      if (checkInTime && checkInTime !== 'Total') {
        const formattedDate = moment(checkInTime).format('MM/DD/YYYY')
        const prevItemCheckInTime =
          index !== 0
            ? moment((rows[index - 1].original as IDriverSessionSearchObject).checkInTime).format('MM/DD/YYYY')
            : ''

        if (formattedDate !== prevItemCheckInTime) {
          acc.push(session)
        }
      } else {
        acc.push(session)
      }
      return acc
    }, [])

    return rowsArr
  }, [rows])

  React.useEffect(() => {
    const newFilters = {
      ...filters,
      timeZone: moment(filters.fromDate).format('Z')
    }
    fetchData({ filters: newFilters, pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])

  return (
    <React.Fragment>
      {loading === true ? (
        <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={`thead-tr-${i}`}>
                {headerGroup.headers.map((column, trIndex) => (
                  <th {...column.getHeaderProps()} key={`tr-th-${i}${trIndex}`}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {filterDuplicateRows.map((row: any, i) => {
              return (
                +prepareRow(row) || (
                  <React.Fragment {...row.getRowProps()}>
                    <tr {...row.getToggleRowExpandedProps()} key={`tr1-${i}`}>
                      {row.cells.map((cell: any, index: any) => {
                        return (
                          <td key={`1-${index}`} {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                    {row.isExpanded && (
                      <tr key={`tr2-${i}`}>
                        <td colSpan={visibleColumns && visibleColumns.length}>
                          {renderRowSubComponent({
                            row,
                            getCheckInCheckOut,
                            getManyTripsPerOperator,
                            filters,
                            openDeleteModal
                          })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              )
            })}
          </tbody>
        </table>
      )}
      <ReactTablePagination
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        totalCount={totalCount}
        nextPage={nextPage}
        previousPage={previousPage}
      />
    </React.Fragment>
  )
}

export default function TableDriverViewSessionHistory(props: ITableDriverSessionProps) {
  const [loading, setLoading] = React.useState(false)
  const [revenue, setRevenue] = React.useState(0)
  const [revenuePerHour, setRevenuePerHour] = React.useState(0)
  const [sessionHistory, setSessionHistory] = React.useState(props.sessionHistory)

  const headerStyles = {
    backgroundColor: '#d9d9d9',
    borderRadius: '65%',
    borderStyle: 'none',
    cursor: 'pointer',
    fontSize: '10.70px',
    textDecorationColor: '#d9d9d9'
  }

  // Create a function that will render our row sub components
  const renderRowSubComponent = React.useCallback(({ row, getCheckInCheckOut, getManyTripsPerOperator, filters }) => {
    if (row.original.checkInTime === 'Total') {
      return <div />
    }

    return (
      <React.Fragment>
        <SubRowAsync
          getCheckInCheckOut={getCheckInCheckOut}
          getManyTripsPerOperator={getManyTripsPerOperator}
          filters={{
            date:
              moment(row.original.modifyDate).format('YYYY-MM-DD') +
              'T00:00:00' +
              moment(row.original.fromDate).format('Z'),
            operatorId: filters.operatorId,
            orgId: props.currentOrganizationId,
            timeZone: moment(row.original.fromDate).format('Z')
          }}
          routeProps={{ match: props.match, history: props.history, location: props.location }}
          deleteSession={props.deleteSession}
        />
      </React.Fragment>
    )
  }, [])

  // since dates are in iso format, we can adjust it using the timezone offset from the client/userDevice.
  // this removes duplicate dates due to timezone
  const getDateWithOffset = (checkInTime: Date) => {
    const offset = new Date().getTimezoneOffset()
    if (offset === 0) {
      return moment(checkInTime).format('MM/DD/YYYY')
    }
    if (offset < 0) {
      return moment(checkInTime)
        .subtract((offset * -1) / 60, 'hours')
        .format('MM/DD/YYYY')
    }
    return moment(checkInTime)
      .add(new Date().getTimezoneOffset() / 60, 'hours')
      .format('MM/DD/YYYY')
  }

  const formatDate = (checkInTime: string | Date | 'Total') => {
    if (checkInTime === 'Total') {
      return <b>{UIMessages.common.total}:</b>
    }
    return moment(checkInTime).isValid() ? moment(checkInTime).format('MM/DD/YYYY') : 'Not Available'
  }

  // TABLE MAIN HEADER  //
  const columns = React.useMemo(
    () => [
      {
        id: 'date',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.Date}>
            Date
          </div>
        ),
        //Header: 'Date',
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          return <div>{formatDate(driver.modifyDate)}</div>
        }
      },
      {
        id: 'total_duration',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.checkIn}>
            Checked In
          </div>
        ),
        accessor: (driver: IDriverSessionSearchObject) => {
          const totalHoursWorked = driver.totalHoursWorked ? secondsToHours(driver.totalHoursWorked) : '00:00'
          return <div>{totalHoursWorked}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'total_break_hours',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.totalBreakHours}>
            Break Hours
          </div>
        ),
        accessor: (driver: IDriverSessionSearchObject) => {
          const breakHours = driver.breakHours ? secondsToHours(driver.breakHours) : '00:00'
          return <div>{breakHours}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'serviceDuration',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.service}>
            Service
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          const serviceDuration = driver.serviceDuration ? secondsToHours(driver.serviceDuration) : '00:00'
          return <div>{serviceDuration}</div>
        }
      },
      {
        id: 'tripDuration',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.trip}>
            Trip
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          const duration = driver.tripDuration ? secondsToHours(driver.tripDuration) : '00:00'
          return <div>{duration}</div>
        }
      },
      {
        id: 'drivingDuration',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.driving}>
            Driving
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          const drivingDuration = driver.drivingDuration ? secondsToHours(driver.drivingDuration) : '00:00'
          return <div>{drivingDuration}</div>
        }
      },
      {
        id: 'estimatedDuration',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.estimated}>
            Estimated
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          const estimatedDuration = driver.estimatedDuration ? secondsToHours(driver.estimatedDuration) : '00:00'
          return <div>{estimatedDuration}</div>
        }
      },
      {
        id: 'distance',
        // Header: 'Distance(mi)',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.distancemi}>
            Distance
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          return <div>{`${metersToMiles(Number(driver.distanceDuration))} miles`}</div>
        }
      },
      {
        id: 'completedTripCount',
        // Header: 'Completed Trip Count',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.ctc}>
            Completed Trips
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          return <div>{driver.completedTripsCount ? driver.completedTripsCount : 0}</div>
        }
      },
      {
        id: 'assignedTripCount',
        // Header: 'Assigned Trip Count',
        Header: () => (
          <div style={{ cursor: 'pointer' }} title={UIMessages.driver.driverSessionsHeaderDefinitions.atc}>
            Assigned Trips
          </div>
        ),
        width: 200,
        accessor: (driver: IDriverSessionSearchObject) => {
          return <div>{driver.assignedTripsCount ? driver.assignedTripsCount : 0}</div>
        }
      }
    ],
    []
  )

  React.useEffect(() => {
    if (props.sessionHistory) {
      const {
        assigned,
        breakHours,
        completed,
        distance,
        driving,
        duration,
        estimated,
        fare,
        revenue,
        service,
        totalHours,
        trip,
        totalHoursWorked
      } = getTotalDetails(props.sessionHistory)
      const revenuePerHour = revenue / 100 / totalHours
      const $PerHour = isNaN(revenuePerHour) ? 0 : revenuePerHour
      const sessionTotalDetails: IDriverSessionSearchObject = {
        assignedTripsCount: assigned,
        assigned_trips_count: assigned,
        breakHours,
        checkInTime: 'Total',
        completedTripsCount: completed,
        completed_trips_count: completed,
        distance,
        distanceDuration: distance,
        drivingDuration: driving,
        driving_duration: driving,
        duration,
        estimatedDuration: estimated,
        estimated_duration: estimated,
        fare,
        modifyDate: 'Total',
        revenue,
        serviceDuration: service,
        service_duration: service,
        tripDuration: trip,
        trip_duration: trip,
        totalHoursWorked
      }
      const sessionHistoryArr = props.sessionHistory
      sessionHistoryArr.push(sessionTotalDetails)
      setRevenue(revenue)
      setRevenuePerHour($PerHour)
      setSessionHistory(sessionHistoryArr)
    }
  }, [props.sessionHistory])

  const fetchData = React.useCallback(async ({ filters, pageSize, pageIndex }) => {
    setLoading(true)
    await props.getDriversSessionList({ ...filters, skip: pageIndex * pageSize, take: pageSize })
    setLoading(false)
  }, [])

  const { filters, openDeleteModal, totalSessions, getCheckInCheckOut, getManyTripsPerOperator } = props
  const pageCount = 1
  const { operatorId } = filters

  return (
    <React.Fragment>
      <Route
        exact={true}
        path={`${props.match.url}/edit-time`}
        render={props => {
          return editTime({ ...props, filters }, operatorId)
        }}
      />
      <div className='font-grey2 bold h5 mt1'>Session History</div>
      <div className='font-grey2 bold h5 mt1'>
        {`Revenue (USD) per hour:`} <b>{revenuePerHour.toFixed(2)}</b>
      </div>
      <div className='react-table no-padding'>
        <Table
          columns={columns}
          data={sessionHistory || []}
          filters={filters}
          loading={loading}
          openDeleteModal={openDeleteModal}
          pageCount={pageCount}
          totalCount={totalSessions}
          fetchData={fetchData}
          getCheckInCheckOut={getCheckInCheckOut}
          getManyTripsPerOperator={getManyTripsPerOperator}
          renderRowSubComponent={renderRowSubComponent}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/drivers/TableDriverViewSessionHistory.tsx